import type { Messages } from './ru';

export const en: Messages = {
  common: {
    back: 'Back',
    logout: 'Sign out',
    themeDark: 'Dark theme',
    themeLight: 'Light theme',
    save: 'Save',
    cancel: 'Cancel',
    create: 'Create',
    close: 'Close',
    copy: 'Copy',
    copied: 'Link copied',
    search: 'Search',
    loading: 'Loading…',
    retry: 'Retry',
    language: 'Русский',
    optional: 'optional',
  },
  nav: {
    packages: 'Packages',
    today: 'Today',
    schedule: 'Schedule',
    people: 'People',
    courses: 'Courses',
    invites: 'Invites',
    settings: 'Settings',
    label: 'Sections',
  },
  subscription: {
    days_one: '{{count}} day',
    days_few: '{{count}} days',
    days_many: '{{count}} days',
    days_other: '{{count}} days',
    trialEnds:
      'Your trial ends on {{date}} — {{days}} left. To continue without a break, write to us: <mail>{{email}}</mail>.',
    ends: 'Your subscription ends on {{date}} — {{days}} left. Renew to keep everything running: <mail>{{email}}</mail>.',
    grace:
      'Your subscription ended on {{date}}. In {{days}} the school will switch to read-only mode. Renew: <mail>{{email}}</mail>.',
    expiredAdmin:
      'Read-only mode: the subscription ended on {{date}}. Schedule and data are visible, but changes are disabled. To renew, write to <mail>{{email}}</mail>.',
    expired: 'The school is temporarily in read-only mode: the schedule is visible, changes are disabled.',
    impersonated: 'Aurea support: you are signed in as {{name}}. The session closes in an hour.',
  },
  roles: { admin: 'Administrator', teacher: 'Teacher', student: 'Student' },
  errors: {
    UNAUTHENTICATED: 'Please sign in again',
    INVALID_CREDENTIALS: 'Wrong e-mail or password',
    FORBIDDEN: 'Not allowed',
    NOT_FOUND: 'Not found',
    VALIDATION_FAILED: 'Check the form fields',
    CONFLICT: 'This record already exists',
    PLAN_LIMIT_REACHED: 'Plan limit reached: {{used}} of {{limit}}. Contact Aurea to add more.',
    SCHEDULE_CONFLICT: 'The teacher has another lesson at this time',
    SUBSCRIPTION_EXPIRED: 'The school subscription has ended; changes are disabled',
    SCHOOL_UNAVAILABLE: 'Access to this school is suspended',
    INVITE_INVALID: 'This link has expired, been revoked or already used',
    RATE_LIMITED: 'Too many attempts, wait a minute',
    CSRF: 'Reload the page and try again',
    INTERNAL: 'Something went wrong. Try again',
  },
  login: {
    title: 'Sign in',
    email: 'E-mail',
    password: 'Password',
    remember: 'Keep me signed in for 30 days',
    submit: 'Sign in',
    forgot: 'Forgot your password?',
  },
  forgot: {
    title: 'Reset password',
    lead: 'Enter the e-mail you sign in with. We will send a link to set a new password.',
    sent: 'If this e-mail is registered at the school, we have sent a link. It is valid for 1 hour.',
    submit: 'Send link',
    back: 'Back to sign in',
  },
  setup: {
    title: 'Create a password',
    hello: '{{name}}, this is your account. Set a password to sign in ({{email}}).',
    password: 'New password',
    hint: 'At least 10 characters',
    submit: 'Save and sign in',
  },
  join: {
    title: 'Sign up',
    invited: 'You are invited to “{{school}}”',
    course: 'Course: {{course}}',
    group: 'group “{{group}}”',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'E-mail',
    phone: 'Phone',
    password: 'Password',
    consent: 'I agree to “{{school}}” processing my personal data',
    submit: 'Sign up',
    emailTaken: 'This e-mail is already registered. Sign in instead.',
    toLogin: 'I already have an account',
  },
  today: {
    title: 'Today',
    now: 'now {{time}}',
    summary: '{{lessons}} today · {{done}} done · {{teachers}}',
    inMinutes: 'in {{count}} min',
    inHours: 'in {{count}} h',
    soon: 'soon',
    mark: 'Mark',
    unmarked: 'not marked',
    donePile: 'Done: {{lessons}}',
    morePile: '{{lessons}} more',
    openDay: 'open day by teacher',
    empty: 'No lessons today',
    emptyHint: 'The week is in the schedule.',
    newLesson: 'New lesson',
  },
  attention: {
    unmarked: 'Needs marking',
    allMarked: 'All marked',
    allMarkedHint: 'Past lessons without attendance will show up here.',
    subscription: 'Aurea subscription',
    plan: '“{{plan}}” plan, paid until {{date}}',
    expired: 'The subscription ended on {{date}}. Changes are disabled, contact Aurea.',
    teachers: 'Teachers',
    students: 'Students',
    of: '{{used}} of {{max}}',
    unlimited: '{{used}}, unlimited',
  },
  schedule: {
    title: 'Schedule',
    day: 'Day by teacher',
    week: 'Week',
    month: 'Month',
    more_one: '{{count}} more',
    more_few: '{{count}} more',
    more_many: '{{count}} more',
    more_other: '{{count}} more',
    allTeachers: 'All teachers',
    teacher: 'Teacher',
    prev: 'Previous',
    next: 'Next',
    today: 'Today',
    scrollHint: '{{teachers}} · scroll sideways to see everyone',
    free: 'free',
    pileLabel: '{{lessons}} from {{from}} to {{to}}: show day by teacher',
    moved: '“{{title}}” moved to {{when}}',
    teacherChanged: '“{{title}}” at {{time}} is now taught by {{teacher}}',
    keyboardHint: 'Arrow keys move the lesson',
    cancelled: 'cancelled',
  },
  feed: {
    button: 'Add to my calendar',
    title: 'Your schedule in your calendar',
    lead: 'Subscribe to your personal link — lessons appear in Google Calendar, Apple Calendar or Outlook and update on their own.',
    get: 'Get the link',
    google: 'Google Calendar: “Other calendars” → “+” → “From URL”.',
    apple: 'iPhone and Mac: Calendar → “Add Subscription” and paste the link.',
    outlook: 'Outlook: “Add calendar” → “Subscribe from web”.',
    secret: 'The link is personal — don’t share it. A new link revokes the previous one.',
  },
  lesson: {
    cancel: 'Cancel lesson',
    cancelConfirm: 'Cancel it',
    cancelReason: 'Reason',
    scopeThis: 'Only this one',
    scopeFollowing: 'This and following',
    scopeAll: 'All upcoming',
    edit: 'Edit lesson',
    seriesHint:
      'Series dates stay the same. Lessons moved by hand keep their time but get the new teacher and duration.',
    changed_one: '{{count}} lesson changed',
    changed_few: '{{count}} lessons changed',
    changed_many: '{{count}} lessons changed',
    changed_other: '{{count}} lessons changed',
    cancelled_one: '{{count}} lesson cancelled',
    cancelled_few: '{{count}} lessons cancelled',
    cancelled_many: '{{count}} lessons cancelled',
    cancelled_other: '{{count}} lessons cancelled',
    isCancelled: 'This lesson is cancelled',
    notStarted: 'Attendance can be marked once the lesson starts.',
    saveMarks: 'Save attendance',
    group: 'group “{{name}}”',
    online: 'Online',
    attendance: 'Mark attendance',
    present: 'Present',
    absent: 'Absent',
    excused: 'Excused',
    complete: 'Complete lesson',
    note: 'Present students are charged from their packages.',
    completed: 'Lesson completed. Charged from packages: {{charged}}.',
    uncharged: 'Without a package: {{count}} — they will appear as debts.',
    noStudents: 'No students in this lesson.',
  },
  lessonForm: {
    title: 'New lesson',
    course: 'Course',
    pickCourse: 'Choose a course',
    group: 'Group',
    pickGroup: 'Choose a group',
    noGroups: 'This course has no groups yet',
    student: 'Student',
    pickStudent: 'Choose a student',
    pickTeacher: 'Choose a teacher',
    courseTeacher: 'teaches the course',
    date: 'Date',
    time: 'Start',
    duration: 'Minutes',
    location: 'Room',
    locationHint: 'Room 2',
    onlineUrl: 'Meeting link',
    urlHint: 'Use a link starting with https://',
    repeat: 'Repeat',
    weekdays: 'On',
    every: 'How often',
    everyWeek: 'Every week',
    everyTwoWeeks: 'Every two weeks',
    end: 'Ends',
    endOpen: 'Never',
    endUntil: 'On a date',
    endCount: 'After a number of lessons',
    untilDate: 'Last day',
    countLabel: 'How many lessons',
    openHint: 'Lessons are created 12 weeks ahead and extended automatically.',
    conflicts: 'The teacher already has lessons at this time:',
    created: 'Created: {{lessons}}',
  },
  billing: {
    days: '{{count}} days',
    ofTotal: 'of {{total}}',
    title: 'Packages',
    templates: 'Package types',
    newTemplate: 'Package type',
    templateName: 'Name',
    templateNameHint: 'For example “English, 8 lessons”',
    appliesTo: 'Valid for',
    anyLesson: 'Any lessons',
    lessonsCount: 'Lessons',
    price: 'Price, ₽',
    validity: 'Valid, days',
    validityHint: 'Empty — no expiry',
    sold: 'Sold: {{count}}',
    noTemplates: 'No package types yet. Create the first one, e.g. “8 lessons a month”.',
    expiring: 'Running out',
    expiringEmpty: 'No packages are running out.',
    debtors: 'Owing',
    debtorsEmpty: 'Nobody owes anything.',
    left: '{{left}} of {{total}}',
    until: 'until {{date}}',
    forever: 'no expiry',
    debtLessons: 'without a package: {{lessons}}',
    owes: 'owes {{amount}}',
    archive: 'Remove',
    sell: 'Sell a package',
    sellTitle: 'New package for {{name}}',
    template: 'Package type',
    custom: 'Custom',
    startsOn: 'Starts',
    paidNow: 'Paid now, ₽',
    method: 'Payment method',
    methods: { cash: 'Cash', card: 'Card', transfer: 'Transfer', other: 'Other' },
    soldToast: 'Package added',
    coveredToast: 'Package added. Lessons charged from debt: {{count}}',
    payment: 'Record payment',
    amount: 'Amount, ₽',
    forPackage: 'For package',
    noPackage: 'Not linked',
    comment: 'Comment',
    paidToast: 'Payment of {{amount}} recorded',
    adjust: 'Adjust',
    adjustTitle: 'Change the number of lessons',
    delta: 'How many to add (or remove with a minus)',
    reason: 'Reason',
    reasonHint: 'Goes to the log',
    adjustedToast: 'Package updated',
    balance: 'Balance',
    balanceOk: 'Nothing owed',
    payments: 'Payments',
    noPayments: 'No payments yet',
    history: 'Lessons',
    noHistory: 'No completed lessons yet',
    charged: 'charged: {{name}}',
    notCharged: 'not charged',
    noPackages: 'No packages yet',
    paidOf: 'paid {{paid}} of {{price}}',
    status: { active: 'active', exhausted: 'used up', expired: 'expired', cancelled: 'cancelled' },
    attendance: { present: 'present', absent: 'absent', excused: 'excused', unmarked: 'not marked' },
    card: 'Student card',
    back: 'All students',
    my: 'My packages',
  },
  people: {
    title: 'People',
    all: 'All',
    add: 'Add',
    search: 'Name, e-mail or phone',
    name: 'Name',
    email: 'E-mail',
    roles: 'Roles',
    courses: 'Courses',
    lastLogin: 'Last sign-in',
    never: 'never',
    owner: 'owner',
    empty: 'Nobody found',
    formTitle: 'New user',
    firstName: 'First name',
    lastName: 'Last name',
    phone: 'Phone',
    role: 'Role',
    created: 'User created. Send them this sign-in link:',
  },
  users: {
    edit: 'Edit',
    editName: 'Edit {{name}}',
    show: 'Show',
    status: { active: 'Active', blocked: 'Blocked', archived: 'Archived' },
    saved: 'Saved',
    sendLink: 'Send sign-in link',
    linkSent: 'Email sent. Older links no longer work. You can also pass the link on yourself:',
    block: 'Block',
    blockHint: 'Can’t sign in. Courses and lessons stay — for a temporary pause.',
    archive: 'Archive',
    archiveHint: 'They left: unenrol from courses and remove from future lessons. History stays.',
    restore: 'Restore',
    restored: 'Access restored',
    blocked: 'Blocked, sessions closed',
    archived: 'Moved to archive',
    teacherLessons_one: 'Done. The teacher still has {{count}} upcoming lesson — assign a substitute',
    teacherLessons_few: 'Done. The teacher still has {{count}} upcoming lessons — assign a substitute',
    teacherLessons_many: 'Done. The teacher still has {{count}} upcoming lessons — assign a substitute',
    teacherLessons_other: 'Done. The teacher still has {{count}} upcoming lessons — assign a substitute',
    emailTaken: 'This email is already used in the school',
    reasons: {
      KEEP_ADMIN: 'The school owner and you yourself can’t lose admin rights',
      SELF_OR_OWNER: 'You can’t block yourself or the school owner',
      NOT_ACTIVE: 'Restore access first',
    },
  },
  import: {
    button: 'Import',
    title: 'Import students from CSV',
    lead: 'Save the sheet from Excel or Google Sheets as CSV. Columns: Last name, First name, Email and optionally Phone — or a single “Full name” column. Headers are optional.',
    pick: 'Choose file',
    found_one: 'Ready to import: {{count}} student',
    found_few: 'Ready to import: {{count}} students',
    found_many: 'Ready to import: {{count}} students',
    found_other: 'Ready to import: {{count}} students',
    bad_one: '{{count}} row with errors will be skipped',
    bad_few: '{{count}} rows with errors will be skipped',
    bad_many: '{{count}} rows with errors will be skipped',
    bad_other: '{{count}} rows with errors will be skipped',
    more: 'and {{count}} more',
    errors: { NAME: 'missing first or last name', EMAIL: 'invalid email' },
    target: 'Enrol in a course',
    noTarget: 'Don’t enrol',
    sendInvites: 'Email students a sign-in link',
    run_one: 'Import {{count}} student',
    run_few: 'Import {{count}} students',
    run_many: 'Import {{count}} students',
    run_other: 'Import {{count}} students',
    done_one: '{{count}} student added',
    done_few: '{{count}} students added',
    done_many: '{{count}} students added',
    done_other: '{{count}} students added',
    invitesSent: 'Sign-in links have been emailed.',
    skipped: {
      EXISTS: 'Row {{row}}: {{email}} already exists in the school — skipped',
      DUPLICATE: 'Row {{row}}: {{email}} repeats in the file — skipped',
    },
  },
  courses: {
    title: 'Courses',
    disciplines: 'Disciplines',
    addDiscipline: 'Discipline',
    addCourse: 'Course',
    disciplineName: 'Discipline name',
    color: 'Paper colour',
    courseName: 'Course name',
    discipline: 'Discipline',
    format: 'Format',
    individual: 'Individual',
    group: 'Group',
    mixed: 'Mixed',
    duration: 'Lesson length, min',
    teachers: 'Teachers',
    groupsCount: 'Groups: {{count}}',
    minutes: '{{count}} min',
    empty: 'No courses yet. Start with a discipline such as “English”.',
  },
  groups: {
    new: 'Group',
    edit: 'Edit group',
    name: 'Group name',
    teacher: 'Teacher',
    noTeacher: 'Not assigned',
    capacity: 'Seats',
    capacityHint: 'Empty means no limit',
    seats: 'Students / seats',
    seatsOf: 'Students: {{count}} of {{capacity}}',
    empty: 'No one in this group yet. Add students below or send them an invite link to this group.',
    add: 'Add a student',
    addButton: 'Add',
    pickStudent: 'Choose a student',
    full: 'No seats left',
    move: 'Move to…',
    moveTo: 'Move {{name}}',
    solo: 'Individually',
    remove: 'Remove from group and course: {{name}}',
    removeFromCourse: 'Unenrol from “{{name}}”',
    removeHint: 'Unenrol from the course. Past lessons and charges stay in the history.',
    archive: 'Archive',
    archiveHint: 'Move the students to another group first',
    courses: 'Courses',
    course: 'Course',
    group: 'Group',
    enroll: 'Enrol in a course',
    enrollButton: 'Enrol',
    allCourses: 'This student is already enrolled in every course.',
    noCourses: 'Not enrolled in any course yet.',
    reasons: {
      GROUP_FULL: 'The group is full ({{capacity}} seats)',
      ALREADY_ENROLLED: 'The student is already enrolled in this course',
      GROUP_NOT_EMPTY: 'The group still has students',
      CAPACITY_BELOW_MEMBERS: 'The group already has {{members}} students — seats can’t be fewer',
    },
  },
  invites: {
    title: 'Invites',
    create: 'New link',
    label: 'Label',
    labelHint: 'For example “B1 intake, September”',
    role: 'Who is invited',
    group: 'Add straight to a group',
    noGroup: 'No group',
    expires: 'Valid for, days',
    maxUses: 'How many times it can be used',
    used: 'Used: {{used}}',
    usedOf: 'Used: {{used}} of {{max}}',
    until: 'until {{date}}',
    revoke: 'Revoke',
    revoked: 'revoked',
    empty: 'No links yet. Create one and send it to students or print a QR code.',
    created: 'Link created and copied. It is shown only now:',
  },
  settings: {
    title: 'School settings',
    look: 'How your school looks',
    name: 'Name',
    color: 'School colour',
    logo: 'Logo',
    logoUpload: 'Upload logo',
    logoReplace: 'Replace',
    logoRemove: 'Remove',
    logoHint: 'PNG, JPEG or WebP. Square works best.',
    timeAndLanguage: 'Time and language',
    timezone: 'Time zone',
    locale: 'Default language',
    localeHint: 'Everyone can switch the language for themselves.',
    workdayStart: 'Working day from',
    workdayEnd: 'to',
    workdayHint: 'The calendar always shows these hours.',
    workdayOrder: 'The day must end after it starts',
    rules: 'Rules',
    rule: {
      chargeAbsent: 'Charge for missed lessons',
      teacherCanReschedule: 'Teachers reschedule their own lessons',
      showGroupMembers: 'Students see their group members',
      notifyLessonChanges: 'Emails about rescheduled and cancelled lessons',
    },
    ruleHint: {
      chargeAbsent: 'A student marked “Absent” is still charged from their package.',
      teacherCanReschedule: 'Otherwise only admins can move lessons.',
      showGroupMembers: 'Classmates’ names are visible in the lesson card.',
      notifyLessonChanges: 'Students and the teacher get an email when a lesson is moved or cancelled.',
    },
    save: 'Save',
    saved: 'Settings saved',
  },
  colors: {
    lemon: 'Lemon',
    apricot: 'Apricot',
    pink: 'Pink',
    lilac: 'Lilac',
    sky: 'Sky',
    mint: 'Mint',
    sage: 'Sage',
    sand: 'Sand',
  },
  plural: {
    lessons_one: '{{count}} lesson',
    lessons_few: '{{count}} lessons',
    lessons_many: '{{count}} lessons',
    lessons_other: '{{count}} lessons',
    teachers_one: '{{count}} teacher',
    teachers_few: '{{count}} teachers',
    teachers_many: '{{count}} teachers',
    teachers_other: '{{count}} teachers',
    students_one: '{{count}} student',
    students_few: '{{count}} students',
    students_many: '{{count}} students',
    students_other: '{{count}} students',
  },
};
